import type { MessageKey, MessagePayloads } from '@ralph/contracts'
import { agents } from './agents.ts'
import { artifacts } from './artifacts.ts'
import { auth } from './auth.ts'
import { folders } from './folders.ts'
import { groups } from './groups.ts'
import { knowledge } from './knowledge.ts'
import { marketplace } from './marketplace.ts'
import { notifications } from './notifications.ts'
import { people } from './people.ts'
import { runs } from './runs.ts'
import { sharing } from './sharing.ts'
import { systems } from './systems.ts'

/**
 * Every sentence the server can ask this client to render (PRD-27 D-5, LOC-032).
 *
 * The type comes from `@ralph/contracts`, not from this file: `MessageKey` and its payloads
 * are the CONTRACT, so adding a key there without writing its sentence here is a compile
 * error, and writing a sentence that interpolates a parameter the server never sends is one
 * too. That check crosses the workspace boundary, which is the whole reason the keys live in
 * the shared package and the words live here with all the other words.
 *
 * Split by domain for the same reason the contract side is: keying ~200 refusals proceeds one
 * domain at a time, and nobody edits a file somebody else is in. This aggregator is the only
 * shared one.
 *
 * ── What is NOT here ──
 *
 * The ~370 internal failures — config, parser, transport, subprocess — have no key and never
 * will. A person was never meant to read them; they fall back to the English `message`, which
 * is the right answer for a line that is really an operator's.
 */
export type ServerMessages = { [K in MessageKey]: (params: MessagePayloads[K]) => string }

export const errors: ServerMessages = {
  'common.request_failed': ({ status }) => `The request failed (${status}).`,
  'common.session_expired': () => 'Your session has ended. Sign in again to continue.',
  'common.connection_lost': () => 'The connection to the assistant was lost.',
  'common.unexpected': () => 'Something went wrong.',
  ...auth,
  ...runs,
  ...artifacts,
  ...knowledge,
  ...folders,
  ...agents,
  ...sharing,
  ...people,
  ...groups,
  ...systems,
  ...marketplace,
  // Not a refusal, and it belongs here anyway: this map is every sentence the SERVER names
  // and this client renders, and a notification is the case where that is most literally true
  // -- the key is on the row and the words are chosen when somebody opens it (D-5 amended).
  ...notifications
}
